/* eslint-disable complexity */
import React, { useMemo, useCallback } from 'react';
import { Grid } from '@elliemae/ds-grid';
import { describe } from '@elliemae/ds-props-helpers';
import { uid } from 'uid';
import { AlertsSmallFill } from '@elliemae/ds-icons';
import type { DSFormLayoutBlockItemT } from './react-desc-prop-types.js';
import { propTypes } from './react-desc-prop-types.js';
import {
  StyledMessage,
  StyledLabel,
  StyledContainer,
  StyledMark,
  ScreenReaderOnly,
  StyledCharCount,
  StyledErrorMessage,
} from './styles.js';
import { useFormLayoutBlockItem } from './config/useFormLayoutBlockItem.js';

const DSFormLayoutBlockItem = (props: DSFormLayoutBlockItemT.Props) => {
  const { globalAttributes, xstyledProps, propsWithDefault } = useFormLayoutBlockItem(props);
  const {
    label,
    feedbackMessage,
    maxCharCounter,
    currentChar,
    validationMessage,
    inputID,
    children,
    hasError,
    leftLabel,
    required,
    optional,
    hideLabel,
    fitContent,
    withHighlight,
    isGroup,
  } = propsWithDefault;

  const { className, label: globalLabel, ...othersGlobalAttributes } = globalAttributes;
  const instanceUID = useMemo(() => inputID ?? `form_layout_block_item_${uid(6)}`, [inputID]);

  const cols = useMemo(() => (maxCharCounter !== undefined ? ['1fr', 'auto'] : ['1fr']), [maxCharCounter]);

  const getOwnerProps = useCallback(() => propsWithDefault, [propsWithDefault]);
  const getOwnerPropsArguments = useCallback(() => ({}), []);
  return (
    <StyledContainer
      leftLabel={leftLabel}
      fitContent={fitContent}
      hideLabel={hideLabel}
      withHighlight={withHighlight}
      as={isGroup ? 'fieldset' : 'div'}
      isGroup={isGroup}
      className={className}
      {...xstyledProps}
      {...othersGlobalAttributes}
      getOwnerProps={getOwnerProps}
      getOwnerPropsArguments={getOwnerPropsArguments}
    >
      <Grid cols={cols} gutter="xxs" aria-live="polite" id={`${instanceUID}_remaining_characters`}>
        {label ? (
          <StyledLabel
            id={`${instanceUID}_block_label`}
            htmlFor={instanceUID}
            leftLabel={leftLabel}
            hideLabel={hideLabel}
            aria-hidden={isGroup}
            getOwnerProps={getOwnerProps}
            getOwnerPropsArguments={getOwnerPropsArguments}
          >
            {label}
            {(required || optional) && (
              <StyledMark required={required} aria-hidden={required}>
                {optional ? <ScreenReaderOnly>Optional</ScreenReaderOnly> : null}
              </StyledMark>
            )}
          </StyledLabel>
        ) : null}

        {maxCharCounter !== undefined && currentChar !== undefined ? (
          <>
            <StyledCharCount
              hasError={currentChar > maxCharCounter}
              getOwnerProps={getOwnerProps}
              getOwnerPropsArguments={getOwnerPropsArguments}
            >
              {`${currentChar}/${maxCharCounter}`}
            </StyledCharCount>
            <ScreenReaderOnly>
              {currentChar < maxCharCounter ? `${maxCharCounter - currentChar} characters remaining` : ''}
            </ScreenReaderOnly>
          </>
        ) : null}
      </Grid>
      {isGroup && (
        <ScreenReaderOnly as="legend" id={`${instanceUID}_legend`}>{`${label}. ${
          (hasError && validationMessage) || (!hasError && feedbackMessage) || ''
        }`}</ScreenReaderOnly>
      )}
      {children}
      {feedbackMessage && !hasError ? (
        <StyledMessage
          aria-live="polite"
          leftLabel={leftLabel}
          hideLabel={hideLabel}
          aria-hidden={isGroup}
          id={`${instanceUID}_feedback_message`}
          getOwnerProps={getOwnerProps}
          getOwnerPropsArguments={getOwnerPropsArguments}
        >
          {feedbackMessage}
        </StyledMessage>
      ) : (
        <ScreenReaderOnly id={`${instanceUID}_feedback_message`} />
      )}
      {validationMessage && hasError ? (
        <StyledErrorMessage
          hasError={hasError}
          leftLabel={leftLabel}
          hideLabel={hideLabel}
          role="alert"
          id={`${instanceUID}_error_message`}
          getOwnerProps={getOwnerProps}
          getOwnerPropsArguments={getOwnerPropsArguments}
        >
          <AlertsSmallFill width={8} height={8} color={['danger', '900']} style={{ marginBottom: '2px' }} />
          &nbsp;
          {`${validationMessage}`}
        </StyledErrorMessage>
      ) : (
        <ScreenReaderOnly id={`${instanceUID}_error_message`} />
      )}
    </StyledContainer>
  );
};

DSFormLayoutBlockItem.displayName = 'DSFormLayoutBlockItem';
const DSFormLayoutBlockItemWithSchema = describe(DSFormLayoutBlockItem);
DSFormLayoutBlockItemWithSchema.propTypes = propTypes;

export { DSFormLayoutBlockItem, DSFormLayoutBlockItemWithSchema };
